import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    HorizontalDivider,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { constants } from '@myphx-engineering/phx-design-system/framework';

import { lillyPharmacy } from '../../../constants';
import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { GetMedicationDocument } from '../../../graphql/generated/graphql';
import { getPricingByNdc } from '../../../util/config/get-pricing';
import { Loader } from '../../common/Loader';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { VerifySwitchPrescriptionPharmacyDetails } from './VerifySwitchPrescriptionPharmacyDetails';

type VerifySwitchPrescriptionDetailsProps = {
    switchedMedicationId: string;
};

export const VerifySwitchPrescriptionDetails = ({
    switchedMedicationId,
}: VerifySwitchPrescriptionDetailsProps) => {
    const { t } = useTranslation();
    const configOverride = useMedicationConfigOverride();

    const { data, loading } = useQuery(GetMedicationDocument, {
        variables: { id: switchedMedicationId },
    });

    if (loading) {
        return <Loader />;
    }
    const medication = getFragment(data?.medication);

    const drugPricing = getPricingByNdc(configOverride, switchedMedicationId);
    const medicationOverride = configOverride.ndcInfo[switchedMedicationId];

    const { description } = medication ?? medicationOverride;

    return (
        <Stack gap="lg">
            <ConditionalComponent condition={drugPricing?.type === 'selfPay'}>
                <Body1>
                    {t('verifySwitchPage.switchToCash.description.line1', {
                        reg: constants.unicode.REGISTERED,
                    })}
                </Body1>

                <Body1 bold>
                    {t('verifySwitchPage.switchToCash.description.line2')}
                </Body1>
            </ConditionalComponent>

            <ConditionalComponent condition={drugPricing?.type === 'insurance'}>
                <Body1>
                    {t('verifySwitchPage.switchToInsurance.description.line1')}
                </Body1>

                <Body1 bold>
                    {t('verifySwitchPage.switchToInsurance.description.line2')}
                </Body1>
            </ConditionalComponent>

            <HorizontalDivider />

            <Stack gap="xxxs">
                <Body1 bold>
                    {t('verifySwitchPage.medicationUpdate.heading')}
                </Body1>

                <Body1>{description}</Body1>
            </Stack>

            <VerifySwitchPrescriptionPharmacyDetails pharmacy={lillyPharmacy} />
        </Stack>
    );
};
